import { Button, Icon, Link } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { getGoogleMapUrl } from 'src/core/modules/trips/helpers/getGoogleMapUrl';

type Props = {
  name: string;
  address: string;
  stars: number;
  reviewsCount: number;
  description: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
};

export const PropertyInfo = ({
  name,
  address,
  stars,
  reviewsCount,
  description,
  coordinates,
}: Props) => {
  const { t } = useTranslation('global');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  const mapsUrl = coordinates
    ? getGoogleMapUrl(`${coordinates.latitude},${coordinates.longitude}`)
    : getGoogleMapUrl(address);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8">
        <div className="flex items-center gap-8">
          <h2 className="title-xl">{name}</h2>
          {stars > 0 && (
            <div className="flex items-center gap-2">
              {Array.from({ length: stars }, (_, index) => index + 1).map(
                (starNumber) => (
                  <Icon
                    key={`star-${starNumber}`}
                    name="star-filled"
                    size="s"
                    className="text-content-warning-default"
                  />
                ),
              )}
            </div>
          )}
        </div>
        <Link
          href={mapsUrl}
          isExternal
          className="body-m text-content-brand-default"
        >
          {address}
        </Link>
        <div className="flex items-center gap-12">
          <div className="flex items-center gap-4">
            <Icon name="star-filled" size="m" />
            <span className="body-m">{stars}</span>
            <span className="body-m text-content-secondary-bg-primary">
              ({reviewsCount})
            </span>
          </div>
        </div>
      </div>
      <p
        className={classNames(
          'body-m text-content-primary mt-24 line-clamp-3',
          {
            'line-clamp-none': isExpanded,
          },
        )}
      >
        {description}
      </p>
      <Button
        variant="tertiaryBrand"
        text={
          isExpanded
            ? t('travel.propertyDetails.propertyInfo.readLess')
            : t('travel.propertyDetails.propertyInfo.readMore')
        }
        iconName={isExpanded ? 'chevron-up' : 'chevron-down'}
        iconPosition="right"
        className="mt-8 -ml-16 self-baseline"
        fit="content"
        onClick={handleReadMore}
      />
    </div>
  );
};
